// app/javascript/components/BioMetricDevices.jsx
import React, { useState, useMemo, useEffect } from 'react';
import { t } from '../utils/i18n';

const BioMetricDevices = ({ onBack, api }) => {
  const [bioMetricDevices, setBioMetricDevices] = useState([]);
  const [locations, setLocations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name-asc');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    device_id: '',
    model: '',
    sdk_version: '',
    device_type: '',
    location_id: '',
    is_active: '',
    department_id: ''
  });

  useEffect(() => {
    fetchBioMetricDevices();
    fetchLocations();
    fetchDepartments();
  }, []);

  const fetchBioMetricDevices = async () => {
    try {
      const data = await api.get('/biometric_devices');
      setBioMetricDevices(data.data.biometric_devices || []);
    } catch (error) {
      console.error('Failed to fetch biometric devices:', error);
      setBioMetricDevices([]);
    }
  };

  const fetchLocations = async () => {
    try {
      const data = await api.get('/locations');
      setLocations(data);
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    }
  };

  const fetchDepartments = async () => {
    try {
      const data = await api.get('/departments');
      setDepartments(data);
    } catch (error) {
      console.error('Failed to fetch departments:', error);
    }
  };

  const filteredAndSortedBioMetricDevices = useMemo(() => {
    let filtered = bioMetricDevices.filter(device =>
      device.device_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      device.model?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      device.sdk_version?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      device.device_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      device.location_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const [field, order] = sortBy.split('-');
    return filtered.sort((a, b) => {
      let aValue, bValue;

      switch(field) {
        case 'name':
          aValue = a.device_id;
          bValue = b.device_id;
          break;
        case 'location':
          aValue = a.location_name || '';
          bValue = b.location_name || '';
          break;
        case 'created':
          aValue = a.created_at || '';
          bValue = b.created_at || '';
          break;
        default:
          aValue = a[field];
          bValue = b[field];
      }

      if (order === 'asc') {
        return aValue.toString().localeCompare(bValue.toString());
      } else {
        return bValue.toString().localeCompare(aValue.toString());
      }
    });
  }, [bioMetricDevices, searchTerm, sortBy]);


  const handleSortSelect = (sortOption) => {
    setSortBy(sortOption);
    setDropdownOpen(false);
  };

  const handleAdd = () => {
    setFormData({ device_id: '', model: '', sdk_version: '', device_type: '', location_id: '', is_active: '', department_id: '' });
    setEditingId(null);
    setShowForm(true);
  };

  const handleEdit = (device) => {
    setFormData({
      device_id: device.device_id || '',
      model: device.model || '',
      sdk_version: device.sdk_version || '',
      device_type: device.device_type || '',
      location_id: device.location_id || '',
      is_active: device.is_active || '',
      department_id: device.department_id || ''
    });
    setEditingId(device.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (confirm('Are you sure you want to delete this biometric device?')) {
      setLoading(true);
      try {
        await api.delete(`/biometric_devices/${id}`);
        setBioMetricDevices(bioMetricDevices.filter(device => device.id !== id));
      } catch (error) {
        console.error('Failed to delete biometric device:', error);
        alert('Failed to delete biometric device');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingId) {
        // Edit existing
        const { response, data } = await api.put(`/biometric_devices/${editingId}`, { biometric_device: formData });
        if (response.ok) {
          setBioMetricDevices(bioMetricDevices.map(device =>
            device.id === editingId ? data : device
          ));
        } else {
          alert('Failed to update biometric device');
        }
      } else {
        // Add new
        const { response, data } = await api.post('/biometric_devices', { biometric_device: formData });
        if (response.ok) {
          setBioMetricDevices([...bioMetricDevices, data]);
        } else {
          alert('Failed to create biometric device');
        }
      }
      setShowForm(false);
      setFormData({ device_id: '', model: '', sdk_version: '', device_type: '', location_id: '', is_active: '', department_id: '' });
      fetchBioMetricDevices();
    } catch (error) {
      console.error('Failed to save biometric device:', error);
      alert('Failed to save biometric device');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setFormData({ device_id: '', model: '', sdk_version: '', device_type: '', location_id: '', is_active: '', department_id: '' });
    setEditingId(null);
  };

  const activeDeviceCount = useMemo(() => {
    return bioMetricDevices.filter(device => device.is_active).length;
  }, [bioMetricDevices]);


  return (
    <>
      {/* Add/Edit Form */}
      {showForm && (
        <div className="mb_40 baseTableWrap">
          <div className="dFlex baseTableHd mb_16">
            <h2 className="txt20 fwMedium colorBlack">
              {editingId ? t('edit_biometric_device') : t('add_biometric_device')}
            </h2>
          </div>
          <form onSubmit={handleSubmit} style={{ padding: '24px 20px' }}>
            <div className="row">
              <div className="col-md-6 mb_20">
                <label className="mb_8">{t('device_id')}</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.device_id}
                  onChange={(e) => setFormData({...formData, device_id: e.target.value})}
                  required
                />
              </div>
              <div className="col-md-6 mb_20">
                <label className="mb_8">{t('model')}</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.model}
                  onChange={(e) => setFormData({...formData, model: e.target.value})}
                  required
                />
              </div>
              <div className="col-md-6 mb_20">
                <label className="mb_8">{t('sdk_version')}</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.sdk_version}
                  onChange={(e) => setFormData({...formData, sdk_version: e.target.value})}
                  required
                />
              </div>
              <div className="col-md-6 mb_20">
                <label className="mb_8">{t('device_type')}</label>
                <select
                  className="form-control"
                  value={formData.device_type}
                  onChange={(e) => setFormData({...formData, device_type: e.target.value})}
                  required
                >
                  <option value="">Select Device Type</option>
                  <option value="photo">Face Recognition (SpeedFace)</option>
                  <option value="fingerprint">Fingerprint Scanner</option>
                  <option value="signature">Signature Pad</option>
                </select>
              </div>
              <div className="col-md-6 mb_20">
                <label className="mb_8">{t('sidebar.location')}</label>
                <select
                  className="form-control"
                  value={formData.location_id}
                  onChange={(e) => setFormData({...formData, location_id: e.target.value})}
                  required
                >
                  <option value="">Select Location</option>
                  {locations.map(type => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-6 mb_20">
                <label className="mb_8">{t('department')}</label>
                <select
                  className="form-control"
                  value={formData.department_id}
                  onChange={(e) => setFormData({...formData, department_id: e.target.value})}
                >
                  <option value="">Select Department (Optional)</option>
                  {departments.map(type => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="col-md-6 mb_20">
              <label className="tccL fwMedium">
                <input
                  type="checkbox"
                  checked={formData.is_active === 'true' || formData.is_active === true}
                  onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                />
                <span className="checkmark"></span>
                {t('active_status')}
              </label>
            </div>

            <div className="dFlex mt_18 btnSet">
              <button type="submit" className="btnBig me-3" disabled={loading}>
                {loading ? t('saving') : (editingId ? t('update') : t('add'))}
              </button>
              <button type="button" className="btnBig btnSecondary" onClick={handleCancel}>
                {t('cancel')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Device List */}
      <div className="mainContentWrap">
        <div className="mainContent">
          <div className="mainContentCT dBlock">
            <h3 className="txt24 fwSemiBold colorBlack mb_16">{t('biometric_integration')}</h3>
            <ul className="dFlex dashFeaturesList col4 mb_28">
                <li>
                    <div className="dashFeaturesBox type1">
                        <div className="dashFeaturesTxt">
                            {t('total_devices')}
                            <span className="txt30 fwBold colorBlack mt_12">{filteredAndSortedBioMetricDevices.length}</span>
                        </div>
                        <div className="dashFeaturesIcon">
                            <img src="/images/biIcon1.png" alt="" />
                        </div>
                    </div>
                </li>
                <li>
                    <div className="dashFeaturesBox type1">
                        <div className="dashFeaturesTxt">
                            {t('device_online')}
                            <span className="txt30 fwBold colorBlack mt_12">{activeDeviceCount}</span>
                        </div>
                        <div className="dashFeaturesIcon">
                            <img src="/images/biIcon2.png" alt="" />
                        </div>
                    </div>
                </li>
                {/* <li>
                    <div className="dashFeaturesBox type1">
                        <div className="dashFeaturesTxt">
                            Pending Sync
                            <span className="txt30 fwBold colorBlack mt_12">15</span>
                        </div>
                        <div className="dashFeaturesIcon">
                            <img src="/images/biIcon3.png" alt="" />
                        </div>
                    </div>
                </li>
                <li>
                    <div className="dashFeaturesBox type1">
                        <div className="dashFeaturesTxt">
                            Mapped Army IDs
                            <span className="txt30 fwBold colorBlack mt_12">1,250</span>
                        </div>
                        <div className="dashFeaturesIcon">
                            <img src="/images/biIcon4.png" alt="" />
                        </div>
                    </div>
                </li> */}
            </ul>
            <div className="biTabs">
                <div className="tabset">
                  <input type="radio" name="tabset" id="tab1" aria-controls="biTab1" defaultChecked/>
                  <label htmlFor="tab1">{t('device_Setup')}</label>
                  <div className="tab-panels">
                      <section id="biTab1" className="tab-panel">
                          <div className="baseTableWrap">
                              <div className="dFlex baseTableHd mb_16">
                                  <h3 className="txt20 fwMedium colorBlack">{t('device_Setup')}</h3>
                                  <div className="dFlex baseTableTR">
                                      <div className="searchBox">
                                        <input
                                          type="text"
                                          placeholder="Search devices..."
                                          value={searchTerm}
                                          onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                        <button type="button">
                                          <img src="/images/search1.png" alt="" />
                                        </button>
                                      </div>
                                      <button type="button" className="tblTopBtn h40 tblTopBtnBlue" onClick={handleAdd}>
                                        <span>{t('add_device')}</span>
                                        <img src="/images/add.png" alt=""/>
                                      </button>
                                      {/* <button type="button" className="tblTopBtn h40 tblTopBtnBlack">
                                          <span>Export Logs</span>
                                          <img src="/images/exportWht.png" alt=""/>
                                      </button> */}
                                      <div className="dropdown dropdownSortBy">
                                        <button
                                          className="dropdown-toggle"
                                          type="button"
                                          onClick={() => setDropdownOpen(!dropdownOpen)}
                                        >
                                          {t('sort_by')}
                                        </button>
                                        <ul className={`dropdown-menu ${dropdownOpen ? 'show' : ''}`}>
                                          <li><a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); handleSortSelect('name-asc'); }}>Name A-Z</a></li>
                                          <li><a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); handleSortSelect('name-desc'); }}>Name Z-A</a></li>
                                          <li><a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); handleSortSelect('location-asc'); }}>Location A-Z</a></li>
                                          <li><a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); handleSortSelect('location-desc'); }}>Location Z-A</a></li>
                                          <li><a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); handleSortSelect('created-desc'); }}>Newest First</a></li>
                                          <li><a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); handleSortSelect('created-asc'); }}>Old First</a></li>
                                        </ul>
                                      </div>
                                  </div>
                              </div>
                              <div className="baseTableContainer">
                                  <table className="baseTable baseTableDepartment">
                                    <thead>
                                      <tr>
                                          <th>{t('device_name')}</th>
                                          <th>{t('type')}</th>
                                          <th>{t('location')}</th>
                                          <th>{t('status')}</th>
                                          <th>{t('model')}</th>
                                          <th>{t('sdk_version')}</th>
                                          <th>{t('last_sync')}</th>
                                          <th style={{ minWidth: "175px !important"}}>{t('actions')}</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {filteredAndSortedBioMetricDevices.length > 0 ? (
                                        filteredAndSortedBioMetricDevices.map((device) => (
                                          <tr key={device.id}>
                                            <td>{device.device_id}</td>
                                            <td>{device.device_type}</td>
                                            <td>{device.location_name}</td>
                                            <td>
                                              <span className={device.is_active ? 'leaveActive' : 'leaveRejected'}>
                                                {device.is_active ? t('active') : t('offline')}
                                              </span>
                                            </td>
                                            <td>{device.model}</td>
                                            <td>{device.sdk_version || 'N/A'}</td>
                                            <td>{device.created_at ? new Date(device.created_at).toISOString().split('T')[0] : 'N/A'}</td>
                                            <td>
                                              <div className="dFlex optBlock">
                                                <a className="optBtn" onClick={() => handleEdit(device)} disabled={loading}>
                                                  <img src="/images/edit.png" alt="" />
                                                  <span>{t('edit')}</span>
                                                </a>
                                                <span className="bar">|</span>
                                                <a href="#" className="optBtn" onClick={() => handleDelete(device.id)} disabled={loading}>
                                                  <img src="/images/delete.png" alt="" />
                                                  <span>{t('delete')}</span>
                                                </a>
                                              </div>
                                            </td>
                                          </tr>
                                        ))
                                      ) : (
                                        <tr>
                                          <td colSpan="8" style={{textAlign: 'center', padding: '20px'}}>
                                            No devices found matching "{searchTerm}"
                                          </td>
                                        </tr>
                                      )}
                                    </tbody>
                                  </table>
                              </div>
                          </div>
                      </section>
                  </div>
                </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BioMetricDevices;
